import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogIn, Upload, Trash2, Image as ImageIcon, Loader2, LayoutDashboard } from "lucide-react";
import api from "@/lib/api";

export default function LoginConfig() {
  const [config, setConfig] = useState({
    logo_url: null,
    background_url: null,
    dashboard_logo_url: null,
    lab_name: "ComaLab"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [uploadingDashboardLogo, setUploadingDashboardLogo] = useState(false);
  
  const logoInputRef = useRef(null);
  const backgroundInputRef = useRef(null);
  const dashboardLogoInputRef = useRef(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get("/login-config");
      setConfig(response.data);
    } catch (error) {
      console.error("Error loading config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file, type) => {
    if (!file) return;

    const setUploadingMap = {
      logo: setUploadingLogo,
      background: setUploadingBackground,
      dashboard_logo: setUploadingDashboardLogo
    };
    const setUploading = setUploadingMap[type];
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("image_type", type);

      const response = await api.post("/login-config/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const fieldMap = {
        logo: "logo_url",
        background: "background_url",
        dashboard_logo: "dashboard_logo_url"
      };

      setConfig(prev => ({
        ...prev,
        [fieldMap[type]]: response.data.url
      }));

      const nameMap = {
        logo: "Logo de inicio de sesión",
        background: "Fondo",
        dashboard_logo: "Logo del dashboard"
      };
      toast.success(`${nameMap[type]} actualizado correctamente`);
    } catch (error) {
      console.error("Error uploading:", error);
      toast.error(error.response?.data?.detail || "Error al subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async (type) => {
    try {
      const fieldMap = {
        logo: "logo_url",
        background: "background_url",
        dashboard_logo: "dashboard_logo_url"
      };
      const fieldName = fieldMap[type];
      
      await api.put("/login-config", {
        ...config,
        [fieldName]: null
      });

      setConfig(prev => ({
        ...prev,
        [fieldName]: null
      }));

      const nameMap = {
        logo: "Logo de inicio de sesión",
        background: "Fondo",
        dashboard_logo: "Logo del dashboard"
      };
      toast.success(`${nameMap[type]} eliminado`);
    } catch (error) {
      toast.error("Error al eliminar la imagen");
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await api.put("/login-config", config);
      toast.success("Configuración guardada");
    } catch (error) {
      toast.error("Error al guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
          Pantalla de Inicio de Sesión
        </h1>
        <p className="text-muted-foreground">
          Personaliza la apariencia de la pantalla de inicio de sesión
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Logo Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Logo del Laboratorio
            </CardTitle>
            <CardDescription>
              Se mostrará en la parte superior del formulario de inicio de sesión
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              {config.logo_url ? (
                <div className="space-y-4">
                  <img
                    src={config.logo_url}
                    alt="Logo"
                    className="max-h-32 mx-auto object-contain"
                  />
                  <div className="flex justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => logoInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Cambiar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveImage("logo")}
                    >
                      <Trash2 className="h-4 w-4 mr-2 text-destructive" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-16 h-16 mx-auto bg-muted rounded-lg flex items-center justify-center">
                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    No hay logo configurado
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                  >
                    {uploadingLogo ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Subir Logo
                  </Button>
                </div>
              )}
            </div>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0], "logo")}
            />
            <p className="text-xs text-muted-foreground">
              Formatos: PNG, JPG, SVG. Tamaño máximo: 5MB
            </p>
          </CardContent>
        </Card>

        {/* Background Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Imagen de Fondo
            </CardTitle>
            <CardDescription>
              Se mostrará como fondo de la pantalla de inicio de sesión
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              {config.background_url ? (
                <div className="space-y-4">
                  <img
                    src={config.background_url}
                    alt="Fondo"
                    className="max-h-32 mx-auto object-cover rounded"
                  />
                  <div className="flex justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => backgroundInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Cambiar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveImage("background")}
                    >
                      <Trash2 className="h-4 w-4 mr-2 text-destructive" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-16 h-16 mx-auto bg-muted rounded-lg flex items-center justify-center">
                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    No hay imagen de fondo configurada
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => backgroundInputRef.current?.click()}
                    disabled={uploadingBackground}
                  >
                    {uploadingBackground ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Subir Fondo
                  </Button>
                </div>
              )}
            </div>
            <input
              ref={backgroundInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0], "background")}
            />
            <p className="text-xs text-muted-foreground">
              Formatos: PNG, JPG. Tamaño máximo: 5MB. Recomendado: 1920x1080
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Dashboard Logo Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5" />
            Logo del Dashboard
          </CardTitle>
          <CardDescription>
            Se mostrará en el menú lateral del sistema (donde dice LabClinic)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed rounded-lg p-6 text-center">
            {config.dashboard_logo_url ? (
              <div className="space-y-4">
                <img
                  src={config.dashboard_logo_url}
                  alt="Logo Dashboard"
                  className="max-h-16 mx-auto object-contain"
                />
                <div className="flex justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => dashboardLogoInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Cambiar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveImage("dashboard_logo")}
                  >
                    <Trash2 className="h-4 w-4 mr-2 text-destructive" />
                    Eliminar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="w-16 h-16 mx-auto bg-muted rounded-lg flex items-center justify-center">
                  <LayoutDashboard className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">
                  No hay logo del dashboard configurado
                </p>
                <Button
                  variant="outline"
                  onClick={() => dashboardLogoInputRef.current?.click()}
                  disabled={uploadingDashboardLogo}
                >
                  {uploadingDashboardLogo ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Subir Logo
                </Button>
              </div>
            )}
          </div>
          <input
            ref={dashboardLogoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files?.[0], "dashboard_logo")}
          />
          <p className="text-xs text-muted-foreground">
            Formatos: PNG, JPG, SVG. Tamaño máximo: 5MB. Recomendado: altura máxima 40px
          </p>
        </CardContent>
      </Card>

      {/* Lab Name Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Nombre del Laboratorio</CardTitle>
          <CardDescription>
            Se mostrará en la pantalla de inicio de sesión si no hay logo configurado
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-md">
            <Label htmlFor="lab_name">Nombre</Label>
            <Input
              id="lab_name"
              value={config.lab_name || ""}
              onChange={(e) => setConfig(prev => ({ ...prev, lab_name: e.target.value }))}
              placeholder="Nombre de tu laboratorio"
            />
          </div>
          <Button onClick={handleSaveConfig} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Guardar Cambios
          </Button>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LogIn className="h-5 w-5" />
            Vista Previa
          </CardTitle>
          <CardDescription>
            Así se verá la pantalla de inicio de sesión
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div 
            className="relative rounded-lg overflow-hidden border"
            style={{ height: "400px" }}
          >
            {/* Background */}
            {config.background_url ? (
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${config.background_url})` }}
              >
                <div className="absolute inset-0 bg-black/40" />
              </div>
            ) : (
              <div className="absolute inset-0 bg-slate-100" />
            )}
            
            {/* Login Card Preview */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white rounded-lg shadow-xl p-6 w-80">
                <div className="text-center mb-4">
                  {config.logo_url ? (
                    <img
                      src={config.logo_url}
                      alt="Logo"
                      className="max-h-16 mx-auto object-contain mb-2"
                    />
                  ) : (
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <div className="p-2 bg-primary rounded-lg">
                        <LogIn className="h-5 w-5 text-white" />
                      </div>
                      <span className="font-bold text-primary">
                        {config.lab_name || "ComaLab"}
                      </span>
                    </div>
                  )}
                  <h3 className="font-semibold">Iniciar Sesión</h3>
                  <p className="text-sm text-muted-foreground">
                    Ingresa tus credenciales
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="h-9 bg-muted rounded-md" />
                  <div className="h-9 bg-muted rounded-md" />
                  <div className="h-9 bg-primary rounded-md" />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
